import { socialPlatforms, productShotFormats, socialPostFormats, lockedSquareFormats } from '@/mocks/app-data';
import StepHeader from './StepHeader';
import type { PostType } from './StepCategory';

interface StepPlatformProps {
  selected: string | null;
  onSelect: (id: string) => void;
  /** Category chosen on Step 1. Drives which set of cards we render here:
   *    'single'       → all social platforms EXCEPT Instagram Carousel
   *    'carousel'     → only Instagram Carousel
   *    'product-shot' → aspect-ratio formats (Square / Vertical / Widescreen
   *                     etc.), not social platforms at all
   *  Optional — /appv2 doesn't have a Category step yet, so it can omit
   *  this prop and the component falls back to showing all platforms (the
   *  pre-restructure behavior). Defensive: Step 1's Continue button gates
   *  against this in practice on /app. */
  postType?: PostType | null;
  /** Compact layout for /appv2's accordion variant. Drops the StepHeader,
   *  uses a 6-up single-row grid at lg, smaller icons, hides the @handle
   *  subtitle. /app passes nothing → falls back to the regular 4-up grid
   *  with full StepHeader chrome. */
  compact?: boolean;
}

export default function StepPlatform({ selected, onSelect, postType, compact }: StepPlatformProps) {
  // ----- Choose data source + header copy by category -----
  const isProductShot = postType === 'product-shot';
  const isCarousel    = postType === 'carousel';
  const isSingle      = postType === 'single';
  const isFashion     = postType === 'fashion';
  const isProductSwap = postType === 'product-swap';
  // Fashion + Product Swap share the same 1:1-locked format pattern in
  // v1, so they both render lockedSquareFormats (1:1 active, 4:5 + 9:16
  // coming soon, no 16:9).
  const isLocked      = isFashion || isProductSwap;

  // Null/undefined postType (e.g. /appv2 calls without it) → fall back to
  // showing ALL platforms unfiltered, matching pre-restructure behavior.
  // 'Multi' badge marks the carousel-supporting platforms (Instagram /
  // Facebook / LinkedIn today). Filter on the badge instead of hardcoding
  // platform ids so adding a new carousel platform later just needs the
  // badge set on its entry in app-data.ts.
  const isCarouselPlatform = (p: { badge?: string }) => p.badge === 'Multi';

  const options = isProductShot
    ? productShotFormats
    : isCarousel
      ? socialPlatforms.filter(isCarouselPlatform)
      : isSingle
        // Social Post route — same icon-row-by-aspect-ratio model as
        // Product Shot. 3 cards (1:1 / 4:5 / 9:16) instead of the old
        // 13 per-platform cards (was redundant since several platforms
        // share each ratio).
        ? socialPostFormats
        : isLocked
          // Fashion + Product Swap: 1:1 active, 4:5 + 9:16 "Coming soon".
          // No 16:9 — awkward for human-subject / hand-held content.
          ? lockedSquareFormats
          : socialPlatforms;

  const headerTitle = isProductShot || isLocked
    ? 'Choose Output Format'
    : isCarousel
      ? 'Choose Your Carousel Platform'
      : isSingle
        ? 'Choose Output Format'
        : 'Choose Your Platform';

  const headerSubtitle = isProductShot
    ? 'Pick the aspect ratio for your product image. Each card lists the platforms that use that size today.'
    : isCarousel
      ? 'Pick where your 4-frame carousel will live. Instagram, Facebook, and LinkedIn all support multi-frame carousels at 1:1.'
      : isSingle
        ? 'Pick the aspect ratio for your post. Each card lists the platforms that use that size today.'
        : isLocked
          ? 'Square is the only output available in v1. Portrait and Vertical are coming soon — they need per-aspect template assets we haven\'t authored yet.'
          : 'Select the social media format you want to create content for. Each platform has optimized dimensions.';

  const headerIcon = isProductShot || isLocked
    ? 'ri-aspect-ratio-line'
    : 'ri-smartphone-line';

  // Compact mode: tighter padding, single-row 6-up grid at lg, no handle
  // subtitle, smaller icon. Used by /appv2 where the section's own
  // header already supplies title + description.
  const gridClass = compact
    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5'
    : 'grid grid-cols-2 lg:grid-cols-4 gap-3';

  return (
    // /app: h-full flex column inside the fixed-viewport wizard panel.
    // /appv2 (compact): no height constraint — content flows in the
    // page's natural scroll.
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon={headerIcon}
          title={headerTitle}
          subtitle={headerSubtitle}
        />
      )}

      {/* Cards grid — square tiles, content vertically organized as
          icon (top-right) / aspect preview (centered, the visual anchor)
          / name + dimensions (bottom). Same shape regardless of data
          source (platforms vs aspect formats). */}
      <div className={gridClass}>
        {options.map((platform) => {
          // productShotFormats entries don't declare `available`; default
          // those to true. Only socialPlatforms entries gate behind the
          // explicit `available: false` flag we set for Coming-soon
          // vertical / story formats.
          const isAvailable =
            (platform as { available?: boolean }).available !== false;
          const isActive = selected === platform.id;

          // Compute preview rectangle in pixels so the shape is identical
          // across screen sizes (no stretching). Long side = a fixed
          // budget; short side derived from the aspect ratio.
          const aspectW = parseFloat(platform.preview.width);
          const aspectH = parseFloat(platform.preview.height);
          const isWide = aspectW >= aspectH;
          const longSide = compact ? 72 : 96;
          const previewW = isWide ? longSide : Math.round(longSide * (aspectW / aspectH));
          const previewH = isWide ? Math.round(longSide * (aspectH / aspectW)) : longSide;

          return (
            <button
              key={platform.id}
              onClick={() => isAvailable && onSelect(platform.id)}
              disabled={!isAvailable}
              className={`group relative aspect-square rounded-2xl border-2 ${compact ? 'p-3' : 'p-4'} transition-all duration-300 flex flex-col ${
                !isAvailable
                  ? 'border-surface-dark/30 bg-white opacity-60 cursor-not-allowed'
                  : isActive
                    ? 'border-primary bg-white shadow-lg shadow-primary/15 cursor-pointer'
                    : 'border-surface-dark/40 bg-white hover:border-primary/40 hover:shadow-md cursor-pointer'
              }`}
            >
              {/* Top row.
                  - Format cards (Product Shot + Social Post): row of small
                    brand-icon badges showing which real platforms use this
                    aspect (left) + active check (right).
                  - Per-platform cards (Carousel route): single brand icon
                    (left) + active check / Multi badge / Coming soon (right).
                  Gate on the presence of `platforms[]` on the entry — that's
                  the distinguishing data shape between format and platform. */}
              <div className="flex items-start justify-between gap-2 min-h-[28px]">
                {(platform as { platforms?: { name: string; icon: string; color: string }[] }).platforms ? (
                  <div className="flex items-center gap-1 flex-wrap">
                    {(platform as { platforms: { name: string; icon: string; color: string }[] }).platforms.map((p) => (
                      <span
                        key={p.name}
                        title={p.name}
                        className={`${compact ? 'w-6 h-6' : 'w-7 h-7'} rounded-md flex items-center justify-center`}
                        style={{ backgroundColor: `${p.color}14` }}
                      >
                        <i
                          className={`${p.icon} ${compact ? 'text-xs' : 'text-sm'}`}
                          style={{ color: p.color }}
                        />
                      </span>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`${compact ? 'w-7 h-7' : 'w-8 h-8'} rounded-lg flex items-center justify-center`}
                    style={{ backgroundColor: `${platform.color}14` }}
                  >
                    <i
                      className={`${platform.icon} ${compact ? 'text-sm' : 'text-base'}`}
                      style={{ color: platform.color }}
                    />
                  </div>
                )}
                {isActive && isAvailable ? (
                  <div className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} rounded-full bg-primary flex items-center justify-center shadow-sm shadow-primary/30 flex-shrink-0`}>
                    <i className="ri-check-line text-white text-xs" />
                  </div>
                ) : !isAvailable ? (
                  <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide flex-shrink-0">
                    Soon
                  </span>
                ) : platform.badge ? (
                  <span
                    className="px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: platform.color }}
                  >
                    {platform.badge}
                  </span>
                ) : null}
              </div>

              {/* Aspect-ratio preview — the visual anchor. Thick stroke,
                  fixed pixel dimensions so the shape reads consistently. */}
              <div className="flex-1 flex items-center justify-center min-h-0">
                <div
                  className={`rounded-md border-[3px] ${isActive && isAvailable ? 'border-primary' : 'border-dark'} transition-colors`}
                  style={{ width: previewW, height: previewH }}
                />
              </div>

              {/* Bottom: name + dimensions, center-aligned. */}
              <div className="text-center">
                <h3 className={`font-display font-bold text-dark leading-tight truncate ${compact ? 'text-sm' : 'text-base'}`}>
                  {platform.name}
                </h3>
                <p className={`text-dark-muted leading-tight truncate ${compact ? 'text-xs mt-0.5' : 'text-sm mt-1'}`}>
                  {platform.aspectRatio} · {platform.dimensions}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}